import React, { useState, useEffect } from 'react';
import cl from './Course.module.css';
import { useParams, Link } from 'react-router-dom';
import AdminService from '../../API/AdminService';
import logger from '../../utils/logger';
import {
    IconArrowLeft, IconEvents, IconClock, IconCalendar,
    IconUser, IconStudents,
} from '../../components/Icons';
const Event = () => {
    const { id } = useParams();
    
    // Состояние данных
    const [event, setEvent] = useState(null);  // Переименовано с course на event
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dates, setDates] = useState([]);
    
    // Состояние интерфейса
    const [saving, setSaving] = useState(false);

    // Загрузка данных при монтировании компонента
    useEffect(() => {
        if (id) {
            loadEventData();  // Переименовано с loadCourseData на loadEventData
        }
    }, [id]);

    // Загрузка данных мероприятия и студентов
    const loadEventData = async () => {
        setLoading(true);
        setError('');
        
        try {
            // Загружаем мероприятие
            const eventResponse = await AdminService.getEvent(id);
            const eventData = eventResponse.data;
            setEvent(eventData);

            // Загружаем всех студентов
            const studentsResponse = await AdminService.getAllStudents();
            const allStudents = studentsResponse.data || [];

            // ИСПРАВЛЕНО: Фильтруем студентов, записанных на это мероприятие (по полю events, а не group)
            const eventStudents = allStudents.filter(student => {
                try {
                    const events = JSON.parse(student.events || '[]');
                    return events.includes(parseInt(id));
                } catch (e) {
                    logger.error('Ошибка парсинга events для студента:', student.id, e);
                    return false;
                }
            });

            setStudents(eventStudents);

            // Генерируем даты мероприятия
            if (eventData.dateStart && eventData.dateEnd) {
                const generatedDates = generateEventDates(eventData);  // Переименовано
                setDates(generatedDates);
            }

        } catch (error) {
            logger.error('Ошибка загрузки данных:', error);
            setError('Ошибка загрузки данных мероприятия');
        } finally {
            setLoading(false);
        }
    };

    // Генерация дат мероприятия.
    //
    // Семантика поля day:
    //   day = 0..6 — еженедельное мероприятие в этот день недели
    //                (Вс=0, Пн=1, ..., Сб=6) → как у курса.
    //   day = -1   — серия подряд идущих дней (лагерь, серия мастер-классов).
    //                Считаем КАЖДЫЙ день между dateStart и dateEnd включительно.
    //   day = null/undefined — fallback: тоже считаем как «каждый день».
    //
    // Раньше при day=-1 цикл `while (currentDate.getDay() !== -1)` никогда
    // не находил совпадение, dates оставался пустым и журнал был «пустой».
    const generateEventDates = (eventData) => {
        const startDate = new Date(eventData.dateStart);
        const endDate = new Date(eventData.dateEnd);
        const dayOfWeek = eventData.day;

        const dates = [];
        let currentDate = new Date(startDate);

        // Лагерь/серия: каждый день в диапазоне
        if (dayOfWeek === -1 || dayOfWeek === null || dayOfWeek === undefined) {
            while (currentDate <= endDate) {
                dates.push(new Date(currentDate));
                currentDate.setDate(currentDate.getDate() + 1);
            }
            return dates;
        }

        // Еженедельный режим: ищем первый нужный день недели
        while (currentDate.getDay() !== dayOfWeek && currentDate <= endDate) {
            currentDate.setDate(currentDate.getDate() + 1);
        }
        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 7);
        }

        return dates;
    };

    // Форматирование даты для отображения (только число)
    const formatDateNumber = (date) => {
        return date.getDate().toString().padStart(2, '0');
    };

    // Форматирование месяца для отображения
    const formatMonth = (date) => {
        return (date.getMonth() + 1).toString().padStart(2, '0');
    };

    // Форматирование даты для сохранения (YYYY-MM-DD)
    const formatDateForSave = (date) => {
        return date.toISOString().split('T')[0];
    };

    // Получение журнала студента для мероприятия
    const getStudentJournal = (student) => {
        try {
            const journal = JSON.parse(student.journal || '{}');
            return journal[id] || { visit: [], pay: [] };
        } catch (e) {
            return { visit: [], pay: [] };
        }
    };

    // Проверка посещения
    const isVisited = (student, date) => {
        const journal = getStudentJournal(student);
        const dateString = formatDateForSave(date);
        return journal.visit?.includes(dateString) || false;
    };

    // Проверка оплаты
    const isPaid = (student, date) => {
        const journal = getStudentJournal(student);
        const dateString = formatDateForSave(date);
        return journal.pay?.includes(dateString) || false;
    };

    // Обновление журнала студента
    const updateStudentJournal = async (student, date, type) => {
        setSaving(true);
        
        try {
            const journal = JSON.parse(student.journal || '{}');
            const eventJournal = journal[id] || { visit: [], pay: [] };
            const dateString = formatDateForSave(date);
            
            // Переключаем состояние
            const currentArray = eventJournal[type] || [];
            const isCurrentlyChecked = currentArray.includes(dateString);
            
            if (isCurrentlyChecked) {
                // Убираем отметку
                eventJournal[type] = currentArray.filter(d => d !== dateString);
            } else {
                // Добавляем отметку
                if (!currentArray.includes(dateString)) {
                    eventJournal[type] = [...currentArray, dateString];
                }
            }
            
            journal[id] = eventJournal;

            // Отправляем обновление на сервер
            await AdminService.updateStudent(student.id, {
                journal: JSON.stringify(journal)
            });

            // Обновляем локальное состояние
            setStudents(prevStudents => 
                prevStudents.map(s => 
                    s.id === student.id 
                        ? { ...s, journal: JSON.stringify(journal) }
                        : s
                )
            );

        } catch (error) {
            logger.error('Ошибка обновления журнала:', error);
            alert('Ошибка сохранения данных');
        } finally {
            setSaving(false);
        }
    };

    // Обработка клика на посещение
    const handleVisitClick = (student, date, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (saving) return;
        updateStudentJournal(student, date, 'visit');
    };

    // Обработка клика на оплату (правая кнопка мыши или Ctrl+клик)
    const handlePayClick = (student, date, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (saving) return;
        updateStudentJournal(student, date, 'pay');
    };

    // Обработка клика на ячейку
    const handleCellClick = (student, date, e) => {
        if (e.ctrlKey || e.metaKey) {
            // Ctrl+клик = оплата
            handlePayClick(student, date, e);
        } else {
            // Обычный клик = посещение
            handleVisitClick(student, date, e);
        }
    };

    // Получение CSS класса для ячейки
    const getCellClass = (student, date) => {
        const visited = isVisited(student, date);
        const paid = isPaid(student, date);
        
        let classes = [cl.journalCell];
        
        if (paid) {
            classes.push(cl.cellPaid);
        }
        if (visited) {
            classes.push(cl.cellVisited);
        }
        
        return classes.join(' ');
    };

    // Получение содержимого ячейки
    const getCellContent = (student, date) => {
        const visited = isVisited(student, date);
        return visited ? '✓' : '';
    };

    // Дни недели
    const daysOfWeek = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <Link to="/main/events" className={cl.backLink}>
                        <IconArrowLeft width={14} height={14}/>
                        <span>К мероприятиям</span>
                    </Link>
                    <div className={cl.noStudents}>Загрузка данных мероприятия…</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <Link to="/main/events" className={cl.backLink}>
                        <IconArrowLeft width={14} height={14}/>
                        <span>К мероприятиям</span>
                    </Link>
                    <div className={cl.error}>{error}</div>
                </div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <Link to="/main/events" className={cl.backLink}>
                        <IconArrowLeft width={14} height={14}/>
                        <span>К мероприятиям</span>
                    </Link>
                    <div className={cl.error}>Мероприятие не найдено</div>
                </div>
            </div>
        );
    }

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Назад */}
                <Link to="/main/events" className={cl.backLink}>
                    <IconArrowLeft width={14} height={14}/>
                    <span>К мероприятиям</span>
                </Link>

                {/* Карточка-шапка мероприятия */}
                <div className={cl.pageHeader}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconEvents width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <h1 className={cl.pageHeaderTitle}>{event.name}</h1>
                            <div className={cl.metaRow}>
                                <span className={cl.metaItem}>
                                    <IconCalendar width={13} height={13}/>
                                    {event.day === -1 || event.day == null
                                        ? 'Серия'
                                        : daysOfWeek[event.day]}
                                </span>
                                <span className={cl.metaItem}>
                                    <IconClock width={13} height={13}/>
                                    {event.timeStart} – {event.timeEnd}
                                </span>
                                {event.prepod && (
                                    <span className={cl.metaItem}>
                                        <IconUser width={13} height={13}/>
                                        {event.prepod}
                                    </span>
                                )}
                                <span className={cl.metaItemMuted}>
                                    {event.dateStart} – {event.dateEnd}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={cl.pageHeaderRight}>
                        <div className={cl.statPill}>
                            <IconStudents width={14} height={14}/>
                            <span className={cl.statPillNum}>{students.length}</span>
                            <span className={cl.statPillLabel}>учеников</span>
                        </div>
                        <div className={cl.statPill}>
                            <IconCalendar width={14} height={14}/>
                            <span className={cl.statPillNum}>{dates.length}</span>
                            <span className={cl.statPillLabel}>занятий</span>
                        </div>
                        {saving && (
                            <div className={cl.savingPill}>
                                <span className={cl.savingDot}/>
                                Сохранение…
                            </div>
                        )}
                    </div>
                </div>



                {/* Таблица журнала */}
                {students.length === 0 ? (
                    <div className={cl.noStudents}>
                        На это мероприятие не записано ни одного ученика
                    </div>
                ) : (
                    <div className={cl.journalContainer}>
                        <div className={cl.journalTable}>
                            {/* Заголовок таблицы */}
                            <div className={cl.tableHeader}>
                                <div className={cl.headerCell}>
                                    <div className={cl.studentHeader}>Ученик</div>
                                </div>
                                <div className={cl.datesHeader}>
                                    {dates.map((date, index) => (
                                        <div key={index} className={cl.dateCell}>
                                            <div className={cl.dateMonth}>{formatMonth(date)}</div>
                                            <div className={cl.dateNumber}>{formatDateNumber(date)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Строки студентов */}
                            <div className={cl.tableBody}>
                                {students.map((student) => (
                                    <div key={student.id} className={cl.studentRow}>
                                        {/* Имя студента (зафиксированный столбец) */}
                                        <div className={cl.studentCell}>
                                            <div className={cl.studentName}>
                                                {student.ferstName} {student.lastName}
                                            </div>
                                            <div className={cl.studentAge}>
                                                {student.age} лет
                                            </div>
                                        </div>

                                        {/* Ячейки для каждой даты */}
                                        <div className={cl.datesRow}>
                                            {dates.map((date, dateIndex) => (
                                                <div
                                                    key={dateIndex}
                                                    className={getCellClass(student, date)}
                                                    onClick={(e) => handleCellClick(student, date, e)}
                                                    onContextMenu={(e) => handlePayClick(student, date, e)}
                                                    title={`${student.ferstName} ${student.lastName} - ${formatMonth(date)}/${formatDateNumber(date)}`}
                                                >
                                                    {getCellContent(student, date)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Подсказка-карточка с легендой и инструкциями */}
                <div className={cl.hintCard}>
                    <div className={cl.hintLeft}>
                        <h3 className={cl.hintTitle}>Как пользоваться</h3>
                        <ul className={cl.hintList}>
                            <li><strong>Клик</strong> — отметить или снять посещение.</li>
                            <li><strong>Ctrl + клик</strong> — отметить или снять оплату.</li>
                        </ul>
                    </div>
                    <div className={cl.hintRight}>
                        <div className={cl.legendItem}>
                            <span className={`${cl.legendSquare} ${cl.cellVisited}`}>✓</span>
                            <span>Присутствовал</span>
                        </div>
                        <div className={cl.legendItem}>
                            <span className={`${cl.legendSquare} ${cl.cellPaid}`}/>
                            <span>Оплачено</span>
                        </div>
                        <div className={cl.legendItem}>
                            <span className={`${cl.legendSquare} ${cl.cellDefault}`}/>
                            <span>Пусто</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Event;